import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { RouteErrorBoundary } from './components/common/RouteErrorBoundary';
import LoginPage from './pages/LoginPage';
import { featureRoutes } from './features/routes';
import { useAuthStore } from './stores/authStore';
import './components/common/styles/entity-form.css';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Derive module name from route path for error boundary.
 * e.g. "/actions" → "Actions", "/context/*" → "Contexte"
 */
function getModuleName(path: string, label?: string): string {
  if (label) return label;
  const clean = path.replace(/^\//, '').replace(/\/\*$/, '').split('/')[0];
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      {featureRoutes.map((route) => {
                        const moduleName = getModuleName(route.path, route.label);
                        const Component = route.component;
                        return (
                          <Route
                            key={route.path}
                            path={route.path}
                            element={
                              <RouteErrorBoundary moduleName={moduleName}>
                                <Suspense fallback={<LoadingSpinner />}>
                                  <Component />
                                </Suspense>
                              </RouteErrorBoundary>
                            }
                          />
                        );
                      })}
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="*" element={
                        <div className="p-6 text-center">
                          <h1 className="text-2xl font-bold text-gray-900">Page non trouvee</h1>
                        </div>
                      } />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;
